import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import {
  MeetingRoom,
  MeetingRoomBooking,
  MeetingRoomBookingResponse,
  MeetingRoomFacilities,
  MeetRoomCapacityBands
} from "./MeetingRooms";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { getMeetingRooms } from '@features/meeting-rooms/MeetingRoomsService';
import { getMeetingRoomBookingsForOfficeV2 } from '@features/meeting-rooms/MeetingRoomV2Service';
import { findMeetingRoomInMeetingRooms, findMeetingRoomInMeetingRoomsByOutlookId } from "./MeetingRoomHelper";
import { selectActiveDay } from '@features/whereabouts/store/editMovements.duck';


export interface RoomFinderState {
  meetingRoomCapacityBand: MeetRoomCapacityBands;
  selectedOffice?: OfficeEntity;
  selectedFloorId?: number;
  meetingRoomFacilities: MeetingRoomFacilities[];
  meetingRooms: MeetingRoom[];
  meetingRoomBooking: MeetingRoomBookingResponse[];
  sidebarOpen: boolean;
  loading: boolean;
}

export const initialRoomFinderState: RoomFinderState = {
  meetingRoomCapacityBand: MeetRoomCapacityBands.ANY,
  meetingRoomFacilities: [],
  meetingRooms: [],
  meetingRoomBooking: [],
  sidebarOpen: true,
  loading: false,
}

export const refreshBookings: any = createAsyncThunk('roomFinder/refreshBookings',
  async(params: any, thunkAPI) => {
    const appState = thunkAPI.getState() as AppState;
    const office = appState.roomFinder.selectedOffice;
    const date = appState.editMovements.activeDay;

    if (office && date) {
      return {
        bookings: await getMeetingRoomBookingsForOfficeV2(office, date),
      }
    }

    return {
      bookings: appState.roomFinder.meetingRoomBooking
    }
  }
)

const filterOutMeetingRoomBooking: any = createAsyncThunk('roomFinder/filterOutBooking',
  async(params: {booking: MeetingRoomBooking}, thunkAPI) => {
    const bookings = (thunkAPI.getState() as AppState).roomFinder.meetingRoomBooking;
    if (params.booking.teamTodayMeetingId) {
      return bookings.filter(b => b.id !== params.booking.teamTodayMeetingId);
    } else if (params.booking.outlookEventUid) {
      return bookings.filter(b => b.id !== params.booking.outlookEventUid);
    } else {
      return bookings;
    }
  }
)

export const loadAllMeetingRoomsForOffice: any = createAsyncThunk(
  'roomFinder/loadAllMeetingRoomsForOffice',
  async (params: {office: OfficeEntity, capacityBands: MeetRoomCapacityBands, facilities: MeetingRoomFacilities[]}, thunkAPI) => {
    const {office, capacityBands, facilities} = params;

    let filteredMeetingRooms: MeetingRoom[] = [];

    if (office) {
      filteredMeetingRooms = await getMeetingRooms(office.id, capacityBands, facilities)
    }

    return {
      meetingRooms: filteredMeetingRooms,
    }
  }
)

export const loadMeetingRoomBookings: any = createAsyncThunk(
  'roomFinder/loadMeetingRoomBookings',
  async (params: {}, thunkAPI) => {
    const state = thunkAPI.getState() as AppState;
    const office = selectedOffice(state);
    const date = selectActiveDay(state);

    if (!office || !date) {
      return { bookings: [] };
    }

    let filteredBookings: MeetingRoomBookingResponse[] = [];

    if (office) {
      filteredBookings = await getMeetingRoomBookingsForOfficeV2(office, date);
    }

    return {
      bookings: filteredBookings,
    }
  }
)

const roomFinderSlice = createSlice({
  name: 'roomFinder',
  initialState: initialRoomFinderState,
  reducers: {
    reset: () => initialRoomFinderState,
    setMeetingRoomBand: (state, action) => ({...state, meetingRoomCapacityBand: action.payload}),
    setMeetingRoomFacilities: (state, action) => ({...state, meetingRoomFacilities: action.payload}),
    setOffice: (state, action) => ({...state, selectedOffice: action.payload, selectedFloorId: undefined}),
    setFloor: (state, action) => ({...state, selectedFloorId: action.payload}),
    setSidebarOpen: (state, action) => ({...state, sidebarOpen: action.payload}),
    setMeetingRoomBookings: (state, action) => ({...state, meetingRoomBooking: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMeetingRoomBookings.pending, (state, action) => ({...state, loading: true, meetingRoomBooking: []}))
      .addCase(loadMeetingRoomBookings.rejected, (state, action) => ({...state, loading: false,}))
      .addCase(loadMeetingRoomBookings.fulfilled, (state, action) => ({
      ...state,
      meetingRoomBooking: action.payload.bookings,
      loading: false,
    }))
      .addCase(refreshBookings.pending, (state) => ({...state, loading: true,}))
      .addCase(refreshBookings.rejected, (state) => ({...state, loading: false,}))
      .addCase(refreshBookings.fulfilled, (state, action) => ({
      ...state,
      meetingRoomBooking: action.payload.bookings,
      loading: false,
    }))
      .addCase(loadAllMeetingRoomsForOffice.pending, (state) => ({...state, loading: true,}))
      .addCase(loadAllMeetingRoomsForOffice.rejected, (state) => ({...state, loading: false,}))
      .addCase(loadAllMeetingRoomsForOffice.fulfilled, (state, action) => ({
      ...state,
      meetingRooms: action.payload.meetingRooms,
      loading: false,
    }))
      .addCase(filterOutMeetingRoomBooking.fulfilled, (state, action) => ({ ...state, meetingRooms: action.payload }));
  }
})

export default roomFinderSlice.reducer;
export const { setMeetingRoomBand, setMeetingRoomFacilities, setOffice, setFloor, setSidebarOpen } = roomFinderSlice.actions;

// Selectors
export const selectedMeetingRoomCapacityBand = (state: AppState) => state.roomFinder.meetingRoomCapacityBand;
export const selectedMeetingRoomFacilities = (state: AppState) => state.roomFinder.meetingRoomFacilities;
export const selectedOffice = (state: AppState) => state.roomFinder.selectedOffice;
export const selectFilteredMeetingRooms = (state: AppState) => state.roomFinder.meetingRooms;
export const filteredMeetingRoomBookings = (state: AppState) => state.roomFinder.meetingRoomBooking;
export const isLoading = (state: AppState) => state.roomFinder.loading;
export const selectIsSidebarOpen = (state: AppState) => state.roomFinder.sidebarOpen;
export const selectedFloorId = (state: AppState) => state.roomFinder.selectedFloorId;
export const selectMeetingRoomById = (state: AppState, roomId: string, outlookMeetingRoomId?: string) => {
  const teamTodayMeetingRoom = findMeetingRoomInMeetingRooms(state.meetingRoomSettings.meetingRooms, roomId);
  if (!teamTodayMeetingRoom && outlookMeetingRoomId) {
    return findMeetingRoomInMeetingRoomsByOutlookId(state.meetingRoomSettings.meetingRooms, outlookMeetingRoomId);
  } else {
    return teamTodayMeetingRoom;
  }
}
